/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import clsx from 'clsx';
import React, { useRef, useLayoutEffect, useMemo, MutableRefObject, useEffect } from 'react';
import { useIntl } from 'react-intl';

import Icon from 'carbon-react/lib/components/icon';

import { LayoutItem as Layout, GridLayout, DroppedEvent } from '../grid-layout';
import { PageCard } from '../page-card/page-card';
import { useReportBuilderContext } from '../../report-builder.context';
import { OnboardingArrows } from '../onboarding/onboarding-arrows/onboarding-arrows';
import { LAYOUT_STEP, WIDTH_STEPS } from '../../constants';
import {
  getLayoutBorder,
  gridVerticalUnitsToPX,
  inchesToPX,
  pageContainsMultiPagesTableFromPrevPages
} from '../../utils-layout';
import { useI18nConstants } from '../../use-i18n-constants';

import styles from './report-renderer.scss';

interface Props {
  isEditable?: boolean;
  setItemToDelete?: (i: string) => void;
  onDuplicateItem?: (i: string) => void;
  printState?: boolean;
  updateLayoutState?: (l: Layout[]) => void;
  onDrop?: (properties: DroppedEvent) => void;
  setPageToDelete?: (i: number) => void;
  isDashboard?: boolean;
  handleCanvasClick?: () => void;
  containerRef: MutableRefObject<HTMLDivElement | null>;
  currency?: string;
  addPageBefore?: (i: number) => void;
  duplicatePage?: (i: number) => void;
  movePageUp?: (i: number) => void;
  movePageDown?: (i: number) => void;
}
export const ReportRenderer = ({
  isEditable,
  printState,
  setItemToDelete,
  onDuplicateItem,
  updateLayoutState,
  onDrop,
  setPageToDelete,
  isDashboard,
  handleCanvasClick,
  containerRef,
  currency,
  addPageBefore,
  duplicatePage,
  movePageUp,
  movePageDown
}: Props) => {
  const intl = useIntl();
  const {
    pageLayout,
    pageLayoutData,
    currentCardId,
    workAreaWidth,
    pageSetup: { margins, backgroundColor, orientation, zoom, showMargins, pageNumbersDisplay },
    headerFooterLimits,
    getPageHeight,
    getPageWidth,
    heightAdjusted,
    pageBreaks,
    setPageBreaks,
    reportId,
    allRequestsFinished,
    isOnboarding
  } = useReportBuilderContext();
  const { getPageNumberLabel } = useI18nConstants();
  const prevReportId = useRef<string>('');
  const isNewReport = useRef<boolean>(false);

  const multiPagesTableStartsInPage = (pageNumber: number) => {
    const pageHeight = getPageHeight();
    const pageStart = pageNumber * pageHeight;
    const pageEnd = (pageNumber + 1) * pageHeight - 1;
    for (const pl of pageLayout) {
      const widgetYStartPosition = gridVerticalUnitsToPX(pl.y);
      const widgetYEndPosition = widgetYStartPosition + gridVerticalUnitsToPX(pl.h);
      // widget starts in page and ends after page
      if (widgetYStartPosition >= pageStart && widgetYStartPosition < pageEnd && widgetYEndPosition > pageEnd) {
        return true;
      }
    }
    return false;
  };

  const layoutChildren = useMemo(() => {
    return pageLayout.map(el => {
      const data = pageLayoutData.find(e => e.i === el.i);
      return (
        data && (
          <div
            key={el.i}
            id={el.i}
            className={clsx([
              'draggableItem',
              'droppableItem',
              styles.pageCardContainer,
              printState && styles.isPrinting,
              currentCardId === el.i && styles.selectedCardContainer
            ])}
            style={{ border: getLayoutBorder(showMargins, isEditable, printState) }}
            // data-grid={el}
            data-color-mode="light"
          >
            {isEditable && setItemToDelete && onDuplicateItem ? (
              <PageCard
                onDelete={() => setItemToDelete(el.i)}
                onDuplicate={() => onDuplicateItem(el.i)}
                cardData={data}
                layoutInfo={el}
                viewOnly={printState}
              />
            ) : (
              <PageCard cardData={data} layoutInfo={el} currency={currency} viewOnly />
            )}
          </div>
        )
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageLayout, pageLayoutData, currentCardId, printState, setItemToDelete, isEditable, currency, showMargins]);

  useEffect(() => {
    if (allRequestsFinished && reportId !== prevReportId.current) {
      prevReportId.current = reportId;
      isNewReport.current = true;
      const view = document.getElementById('viewContainer');
      view && view.scrollTo(0, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId, allRequestsFinished, prevReportId.current]);

  useLayoutEffect(() => {
    const adjustCardsHeight = () => {
      const actualHeight =
        +(containerRef.current?.querySelector('.react-grid-layout') as HTMLElement)?.style?.height.replace('px', '') ||
        containerRef.current?.clientHeight ||
        1;
      const pageHeight = getPageHeight();
      // actual height is rounded number which is good when real height decimal is below .5. When decimal is above .5 then we
      // have case where there are to many pages on screen because of Math.ceil. For example when we open a report in landscape
      // mode real height is 826.6666... and actualHeight is going to have value 827. Math.ceil(827/826.66666) = 2 which
      // will result in 2 page breaks. To stop that behaviour, actualHeight is reduced by 1px.
      const breaks = Math.ceil((actualHeight - 1) / pageHeight);
      if (breaks > pageBreaks || isNewReport.current) {
        setPageBreaks(breaks);
      }
      const limits = [];
      const stepsPerPage = Math.floor(
        (pageHeight - inchesToPX(margins[0].value) - inchesToPX(margins[2].value)) / LAYOUT_STEP
      );
      for (let i = 0; i < breaks; i++) {
        limits.push([
          Math.floor((i * pageHeight) / LAYOUT_STEP) + stepsPerPage,
          Math.ceil(((i + 1) * pageHeight) / LAYOUT_STEP)
        ]);
      }
      if (JSON.stringify(headerFooterLimits.current) !== JSON.stringify(limits)) {
        headerFooterLimits.current = limits;
        updateLayoutState && updateLayoutState(pageLayout);
      }
    };
    if ((!heightAdjusted.current || isNewReport.current) && orientation !== 'dashboard') {
      adjustCardsHeight();
      heightAdjusted.current = true;
      isNewReport.current = false;
    }

    window.addEventListener('resize', adjustCardsHeight);

    return () => {
      window.removeEventListener('resize', adjustCardsHeight);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orientation, containerRef.current?.clientHeight, heightAdjusted.current, isNewReport.current]);

  const renderGridLayout = () => (
    <GridLayout
      draggableHandle=".drag-handle"
      isDraggable={isEditable && !isOnboarding}
      isDroppable={isEditable}
      isResizable={isEditable && !isOnboarding}
      cols={WIDTH_STEPS}
      rowHeight={LAYOUT_STEP}
      width={
        // 80 for default left and right margins
        isDashboard ? workAreaWidth + 80 : getPageWidth() - inchesToPX(margins[1].value) - inchesToPX(margins[3].value)
      }
      transformScale={zoom}
      style={
        isEditable
          ? {
              backgroundColor: isDashboard ? backgroundColor : '',
              minHeight: isDashboard
                ? '100vh'
                : pageBreaks * getPageHeight() - inchesToPX(margins[0].value) - inchesToPX(margins[2].value),
              margin: '0 auto',
              width: isDashboard
                ? workAreaWidth + 80
                : getPageWidth() - inchesToPX(margins[1].value) - inchesToPX(margins[3].value)
            }
          : {}
      }
      layout={isEditable ? pageLayout : pageLayout.map(pl => ({ ...pl, isDraggable: false }))}
      onDrop={isEditable ? onDrop : () => null}
      onLayoutChange={updateLayoutState}
      // onDragStop={updateLayoutState} // helps fix updating layout when allowOverlap is on
      containerPadding={[0, 0]}
      margin={[0, 0]}
      resizeHandles={['s', 'e', 'se']}
      preventCollision
      compactType={null}
      // allowOverlap // currently causing a lot of issues
    >
      {layoutChildren}
    </GridLayout>
  );

  return isDashboard ? (
    <>
      {isOnboarding && <OnboardingArrows position="report" />}
      {renderGridLayout()}
    </>
  ) : (
    <div
      className={clsx(styles.pagesContainer, printState && styles.printing)}
      style={{
        paddingTop: inchesToPX(margins[0].value),
        paddingRight: inchesToPX(margins[1].value),
        paddingBottom: inchesToPX(margins[2].value),
        paddingLeft: inchesToPX(margins[3].value),
        backgroundColor,
        width: getPageWidth(),
        minHeight: pageBreaks * getPageHeight(),
        zoom: printState ? 1 : !isEditable ? 1 : zoom
      }}
      onClick={handleCanvasClick ? handleCanvasClick : () => null}
      ref={containerRef}
    >
      {printState && (
        <style type="text/css" media="print">
          {`\
          @page { size: ${getPageWidth()}px ${getPageHeight()}px; margin: 0 0 0 0 !important; }\
        `}
        </style>
      )}
      {isOnboarding && <OnboardingArrows position="report" />}
      <div
        className={clsx(styles.pageBorderContainer)}
        style={{
          top: inchesToPX(margins[0].value),
          width: `calc(100% - ${inchesToPX(margins[1].value) + inchesToPX(margins[3].value)}px)`
        }}
      >
        {pageBreaks > 0 &&
          Array.from({ length: pageBreaks }, (_, i) => i).map(e => (
            <div
              key={`page-${e}`}
              style={{
                zIndex: 1,
                position: 'absolute',
                top: (e + 1) * getPageHeight() - inchesToPX(margins[0].value) - 32,
                right: -inchesToPX(margins[1].value) + 30,
                fontSize: 12
              }}
            >
              {getPageNumberLabel(e + 1, pageBreaks, pageNumbersDisplay)}
            </div>
          ))}
        {pageBreaks > 0 ? (
          Array.from({ length: pageBreaks }, (_, i) => i).map(e => {
            const multiPagesTableStarts = multiPagesTableStartsInPage(e);
            const multiPagesTableFromPrevPages = pageContainsMultiPagesTableFromPrevPages(
              e,
              getPageHeight(),
              pageLayout
            );
            const multiPagesTableStartsInNextPage = multiPagesTableStartsInPage(e + 1);
            const multiPagesTableFromPrevPagesInPrevPage =
              e > 0 && pageContainsMultiPagesTableFromPrevPages(e - 1, getPageHeight(), pageLayout);
            return (
              <div
                className={clsx(styles.pageBorder)}
                key={e}
                style={{
                  border: getLayoutBorder(showMargins, isEditable, printState),
                  height: getPageHeight() - inchesToPX(margins[0].value) - inchesToPX(margins[2].value),
                  marginBottom: e < pageBreaks - 1 ? inchesToPX(margins[0].value) + inchesToPX(margins[2].value) : 0
                }}
              >
                {!printState && (
                  <div
                    className={clsx(styles.pageBreak)}
                    style={{
                      bottom: -inchesToPX(margins[2].value) - 1,
                      width: `calc(100% + ${inchesToPX(margins[1].value) + inchesToPX(margins[3].value) + 90}px)`,
                      left: -inchesToPX(margins[3].value) - 45
                    }}
                  />
                )}
                {isEditable && !isOnboarding && (
                  <div
                    className={styles.pageActions}
                    style={{ right: -inchesToPX(margins[1].value) - 42, top: -inchesToPX(margins[0].value) + 10 }}
                  >
                    {addPageBefore && !multiPagesTableFromPrevPages && (
                      <span
                        title={intl.formatMessage({
                          id: 'reporting.insert_new_page_before_page',
                          defaultMessage: 'Insert a new page before this page'
                        })}
                        className={styles.addPageIcon}
                        onClick={() => addPageBefore(e)}
                      >
                        <Icon type="add" />
                      </span>
                    )}
                    {duplicatePage && !multiPagesTableFromPrevPages && !multiPagesTableStarts && (
                      <span
                        title={intl.formatMessage({
                          id: 'reporting.duplicate_page_to_new_page',
                          defaultMessage: 'Duplicate page content to a new page'
                        })}
                        className={styles.duplicatePageIcon}
                        onClick={() => duplicatePage(e)}
                      >
                        <Icon type="duplicate" />
                      </span>
                    )}
                    {pageBreaks > 1 && setPageToDelete && !multiPagesTableFromPrevPages && (
                      <span
                        title={intl.formatMessage({
                          id: 'reporting.delete_this_page',
                          defaultMessage: 'Delete this page'
                        })}
                        className={styles.deleteIcon}
                        onClick={() => setPageToDelete(e)}
                      >
                        <Icon type="delete" />
                      </span>
                    )}
                    {pageBreaks > 1 &&
                      e > 0 &&
                      movePageUp &&
                      !multiPagesTableFromPrevPages &&
                      !multiPagesTableStarts &&
                      !multiPagesTableFromPrevPagesInPrevPage && (
                        <span
                          title={intl.formatMessage({
                            id: 'reporting.move_page_up',
                            defaultMessage: 'Move page up'
                          })}
                          className={styles.deleteIcon}
                          onClick={() => movePageUp(e)}
                        >
                          <Icon type="arrow_up" />
                        </span>
                      )}
                    {pageBreaks > 1 &&
                      e < pageBreaks - 1 &&
                      movePageDown &&
                      !multiPagesTableFromPrevPages &&
                      !multiPagesTableStarts &&
                      !multiPagesTableStartsInNextPage && (
                        <span
                          title={intl.formatMessage({
                            id: 'reporting.move_page_down',
                            defaultMessage: 'Move page down'
                          })}
                          className={styles.deleteIcon}
                          onClick={() => movePageDown(e)}
                        >
                          <Icon type="arrow_down" />
                        </span>
                      )}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div />
        )}
      </div>
      {renderGridLayout()}
    </div>
  );
};
